import React, { useState, useRef } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { motion } from 'framer-motion';
import { Mic, Send, MicOff } from 'lucide-react';
import AIService from '../../services/AIService';

interface ChatInputProps {
  onSendMessage: (message: string) => void;
  onSendVoiceText: (text: string) => void;
  disabled?: boolean;
}

const ChatInput: React.FC<ChatInputProps> = ({ onSendMessage, onSendVoiceText, disabled = false }) => {
  const [message, setMessage] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [recordingError, setRecordingError] = useState<string | null>(null);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const textareaRef = useRef<HTMLInputElement>(null);

  // Web Speech API recognition
  const recognitionRef = useRef<any>(null);

  const startWebSpeechRecognition = () => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = (window as any).webkitSpeechRecognition || (window as any).SpeechRecognition;
      recognitionRef.current = new SpeechRecognition();
      
      recognitionRef.current.continuous = false;
      recognitionRef.current.interimResults = false;
      recognitionRef.current.lang = 'es-ES';
      
      recognitionRef.current.onstart = () => {
        console.log('🎤 Web Speech API started');
        setIsTranscribing(true);
        setRecordingError(null);
      };
      
      recognitionRef.current.onresult = (event: any) => {
        const transcript = event.results[0][0].transcript;
        console.log('✅ Web Speech API result:', transcript);
        
        setIsRecording(false);
        setIsTranscribing(false);
        
        // Send transcription immediately using ultra-fast endpoint
        onSendVoiceText(transcript);
      };
      
      recognitionRef.current.onerror = (event: any) => {
        console.error('❌ Web Speech API error:', event.error);
        setIsRecording(false);
        setIsTranscribing(false);
        setRecordingError(`Error de reconocimiento: ${event.error}`);
      };
      
      recognitionRef.current.onend = () => {
        console.log('🔚 Web Speech API ended');
        setIsRecording(false);
        setIsTranscribing(false);
      };
      
      recognitionRef.current.start();
      return true;
    }
    return false;
  };

  const handleTestSmartQuery = async () => {
    console.log('🧪 Testing smart query system...');
    const testQuestions = [
      "¿Qué dispositivos alertan sobre geocercas de riesgo en casas de empeño?",
      "¿Cuántos GPS reportan en geocercas de lotes de autos?",
      "¿Qué equipos detectan problemas en perímetros de casas de empeño?"
    ];
    
    const randomQuestion = testQuestions[Math.floor(Math.random() * testQuestions.length)];
    
    try {
      console.log(`🎯 Testing with question: "${randomQuestion}"`);
      onSendVoiceText(randomQuestion);
    } catch (error) {
      console.error('🧪 Test error:', error);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (message.trim() && !disabled) {
      onSendMessage(message.trim());
      setMessage('');
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setMessage(e.target.value);
  };

  const handleVoiceRecord = async () => {
    if (isRecording || isTranscribing) {
      // Stop recording
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
      setIsRecording(false);
      setIsTranscribing(false);
      return;
    }

    // Check Web Speech API support
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      console.log('🚀 Using Web Speech API for instant transcription');
      setIsRecording(true);
      
      if (startWebSpeechRecognition()) {
        return; // Web Speech API started successfully
      }
    }

    // If Web Speech API is not available, show error
    console.log('❌ Web Speech API not available in this browser');
    setRecordingError('Tu navegador no soporta reconocimiento de voz. Usa Chrome, Edge o Safari.');
  };

  return (
    <ChatInputContainer>
      {recordingError && (
        <ErrorMessage
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
        >
          {recordingError}
        </ErrorMessage>
      )}
      
      <InputWrapper>
        <ChatInputField
          ref={textareaRef}
          type="text"
          value={message}
          onChange={handleInputChange}
          onKeyPress={handleKeyPress}
          placeholder={
            disabled 
              ? "Piedad está pensando..." 
              : isRecording 
                ? "Grabando audio..." 
                : "Escribe tu mensaje o usa el micrófono..."
          }
          disabled={disabled || isRecording}
        />
        
        {/* Test Button - Smart Query - Más visible */}
        <button 
          type="button" 
          onClick={handleTestSmartQuery}
          style={{
            position: 'absolute',
            top: '-50px',
            left: '50%',
            transform: 'translateX(-50%)',
            padding: '8px 15px',
            background: '#28a745',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: 'bold',
            cursor: 'pointer',
            boxShadow: '0 2px 8px rgba(40, 167, 69, 0.3)',
            zIndex: 1000
          }}
        >
          🧠 Test Smart Query
        </button>

        {/* Voice Button */}
        <VoiceButton
          type="button"
          onClick={handleVoiceRecord}
          disabled={disabled}
          isRecording={isRecording || isTranscribing}
          whileHover={{ scale: 1.1, rotate: 15 }}
          whileTap={{ scale: 0.95 }}
        >
          {(isRecording || isTranscribing) ? <MicOff size={20} /> : <Mic size={20} />}
          <ActionIndicator>
            {isTranscribing ? 'Transcribiendo...' : 
             isRecording ? 'Detener grabación' : 'Grabar mensaje de voz'}
          </ActionIndicator>
        </VoiceButton>
        
        {/* Send Button */}
        <SendButton
          type="submit"
          onClick={handleSubmit}
          disabled={!message.trim() || disabled || isRecording}
          whileHover={{ scale: 1.1, rotate: 15 }}
          whileTap={{ scale: 0.95 }}
        >
          <Send size={20} />
          <ActionIndicator>
            Enviar mensaje
          </ActionIndicator>
        </SendButton>
      </InputWrapper>
      
      {isRecording && (
        <RecordingIndicator
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.8 }}
        >
          <RecordingDot />
          <span>Grabando... Haz clic en el micrófono para detener</span>
        </RecordingIndicator>
      )}
    </ChatInputContainer>
  );
};

// Keyframes
const recordingPulse = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.05); }
`;

const voiceRipple = keyframes`
  0% { transform: scale(0.8); opacity: 1; }
  100% { transform: scale(1.5); opacity: 0; }
`;

// Styled Components
const ChatInputContainer = styled.div`
  padding: 25px;
  background: rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(20px);
  border-top: 1px solid rgba(255, 255, 255, 0.1);
`;

const InputWrapper = styled.div`
  display: flex;
  gap: 12px;
  background: rgba(255, 255, 255, 0.95);
  border-radius: 30px;
  padding: 8px;
  box-shadow: 
    0 10px 30px rgba(0, 0, 0, 0.3),
    inset 0 1px 0 rgba(255, 255, 255, 0.5);
  transition: all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(152, 1, 46, 0.1), transparent);
    transition: left 0.5s ease;
  }

  &:focus-within {
    transform: translateY(-5px) scale(1.02);
    box-shadow: 
      0 15px 40px rgba(152, 1, 46, 0.4),
      inset 0 1px 0 rgba(255, 255, 255, 0.8);
  }

  &:focus-within::before {
    left: 100%;
  }
`;

const ChatInputField = styled.input`
  flex: 1;
  border: none;
  outline: none;
  padding: 18px 24px;
  font-size: 16px;
  background: transparent;
  color: #2c0a0f;

  &::placeholder {
    color: #686869;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const VoiceButton = styled(motion.button)<{ isRecording: boolean }>`
  width: 55px;
  height: 55px;
  border: none;
  background: ${props => props.isRecording 
    ? 'linear-gradient(135deg, #ff4444, #ff6666)'
    : 'linear-gradient(135deg, #722F37, #98012E)'};
  border-radius: 50%;
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  margin-right: 5px;

  &:hover {
    transform: scale(1.1) rotate(15deg);
    box-shadow: 0 8px 20px rgba(114, 47, 55, 0.5);
  }

  &:active {
    transform: scale(0.95);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 0;
    height: 0;
    background: radial-gradient(circle, rgba(255, 255, 255, 0.5), transparent);
    border-radius: 50%;
    transition: all 0.4s ease;
    transform: translate(-50%, -50%);
  }

  &:active::before {
    width: 120px;
    height: 120px;
  }

  ${props => props.isRecording && css`
    animation: ${recordingPulse} 1s infinite;
    
    &::after {
      content: '';
      position: absolute;
      top: -10px;
      left: -10px;
      right: -10px;
      bottom: -10px;
      border: 3px solid rgba(255, 68, 68, 0.5);
      border-radius: 50%;
      animation: ${voiceRipple} 1.5s infinite;
    }
  `}
`;

const SendButton = styled(motion.button)`
  width: 55px;
  height: 55px;
  border: none;
  background: linear-gradient(135deg, #722F37, #98012E);
  border-radius: 50%;
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;

  &:hover {
    transform: scale(1.1) rotate(15deg);
    box-shadow: 0 8px 20px rgba(114, 47, 55, 0.5);
  }

  &:active {
    transform: scale(0.95);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    background: rgba(114, 47, 55, 0.3);
  }

  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 0;
    height: 0;
    background: radial-gradient(circle, rgba(201, 35, 35, 0.5), transparent);
    border-radius: 50%;
    transition: all 0.4s ease;
    transform: translate(-50%, -50%);
  }

  &:active::before {
    width: 120px;
    height: 120px;
  }
`;

const ActionIndicator = styled.div`
  position: absolute;
  top: -40px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 12px;
  opacity: 0;
  transition: all 0.3s ease;
  pointer-events: none;
  white-space: nowrap;

  ${VoiceButton}:hover &,
  ${SendButton}:hover & {
    opacity: 1;
    top: -50px;
  }
`;

const ErrorMessage = styled(motion.div)`
  background: linear-gradient(135deg, #ff4444, #cc3333);
  color: white;
  padding: 12px 20px;
  border-radius: 10px;
  margin-bottom: 15px;
  font-size: 14px;
  box-shadow: 0 4px 15px rgba(255, 68, 68, 0.3);
`;

const RecordingIndicator = styled(motion.div)`
  display: flex;
  align-items: center;
  gap: 10px;
  background: rgba(255, 68, 68, 0.1);
  border: 1px solid rgba(255, 68, 68, 0.3);
  padding: 15px 20px;
  border-radius: 15px;
  margin-top: 15px;
  color: #ff4444;
  font-size: 14px;
  font-weight: 500;
`;

const RecordingDot = styled.div`
  width: 12px;
  height: 12px;
  background: #ff4444;
  border-radius: 50%;
  animation: ${recordingPulse} 1s infinite;
`;

export default ChatInput;
